import { useQueryClient } from '@tanstack/react-query';

import { useFishGroupsController_revertOperation } from '@/services/hooks';

import type { MutationState } from '../shared/mutation';

import {
    invalidateAllFishGroupQueries,
    invalidateClientLineageQueries,
    invalidateClientOverviewQueries,
    invalidateFishGroupQueries,
} from './_invalidateQueries';
import { isCompleteRevertResponse, mapRevertOperationResultToDomain } from './revertMappers';
import type { RevertOperationRequest, RevertOperationResult } from './revertTypes';

interface UseRevertFishGroupOperationOptions {
    onSuccess?: (result: RevertOperationResult) => void;
    onError?: (error: unknown) => void;
}

export interface UseRevertFishGroupOperationResult extends MutationState {
    revert: (requestBody: RevertOperationRequest) => Promise<RevertOperationResult>;
}

/**
 * Mutation hook for `POST /:clientId/operations/:operationId/revert`. Like an
 * operation, one revert touches several groups (every group restored and every
 * group discarded), so the read queries of all of them are refreshed, plus every
 * cached lineage graph of the client, since linked groups' graphs change too, and
 * the client overview, which names each pen's active group. When the
 * response does not name every group it touched, every cached fish-group query of
 * the client is refreshed instead, so no view keeps the state from before.
 */
export const useRevertFishGroupOperation = (
    clientId: number,
    operationId: number,
    options?: UseRevertFishGroupOperationOptions
): UseRevertFishGroupOperationResult => {
    const queryClient = useQueryClient();
    const mutation = useFishGroupsController_revertOperation();

    const revert = async (requestBody: RevertOperationRequest): Promise<RevertOperationResult> => {
        try {
            const response = await mutation.mutateAsync({ clientId, operationId, requestBody });
            // The generated client types the body as `any`; the mapper checks it.
            const result = mapRevertOperationResultToDomain(response.data, operationId);
            const impactedIds = [...result.restoredFishGroupIds, ...result.discardedFishGroupIds];
            if (isCompleteRevertResponse(response.data) && impactedIds.length > 0) {
                await Promise.all([
                    ...impactedIds.map((id) =>
                        invalidateFishGroupQueries(queryClient, clientId, id)
                    ),
                    // Linked groups' graphs change too, and the response does not name them.
                    invalidateClientLineageQueries(queryClient, clientId),
                ]);
            } else {
                await invalidateAllFishGroupQueries(queryClient, clientId);
            }
            // The client and location overviews name each pen's active group.
            await invalidateClientOverviewQueries(queryClient, clientId);
            options?.onSuccess?.(result);
            return result;
        } catch (error) {
            options?.onError?.(error);
            throw error;
        }
    };

    return {
        revert,
        isLoading: mutation.isPending,
        error: mutation.error,
        reset: mutation.reset,
    };
};
